import { t as tr } from './i18n';
import { intlLocale } from './i18n';
import type { DashOrder, DashRfq } from './cockpit';
import type { RequisitionCard } from './dashboard';
import type { SupplierRef } from './types';

/**
 * Cifrele noi de pe „Panou de control” (2026-10-13, după Precoro): săptămâna
 * care vine, timpul de la cerere la comandă, cât răspund furnizorii, unde se
 * duc banii. Doar fapte din datele panoului; „azi” e al browserului.
 */

const DAY = 86_400_000;
const at = (day: string) => new Date(`${day}T12:00:00Z`).getTime();

/** 'AAAA-LL-ZZ' + n zile */
export function addDays(day: string, n: number): string {
  return new Date(at(day) + n * DAY).toISOString().slice(0, 10);
}

export type WeekKind = 'delivery' | 'deadline' | 'needed';

export interface WeekItem {
  key: string;
  kind: WeekKind;
  /** 'AAAA-LL-ZZ' */
  day: string;
  title: string;
  detail: string;
  /** lei fără TVA, la livrări */
  lei: number | null;
  rfqId?: string;
  orderId?: string;
  requisitionId?: string;
}

export interface WeekAhead {
  /** zilele, de azi încolo, fiecare cu ce are */
  days: { day: string; items: WeekItem[] }[];
  /** trecute de dată și încă neîntâmplate: livrări care n-au venit, necesar care trebuia */
  overdue: WeekItem[];
  /** cât se livrează (după data dorită) în zilele astea */
  deliveryLei: number;
  deliveries: number;
  deadlines: number;
  needed: number;
}

/**
 * Ce vine în următoarele `n` zile: livrările după data dorită de pe comandă,
 * termenele de răspuns ale cererilor deschise, ziua în care materialul trebuie
 * pe șantier (cereri fără comandă și necesar nepreluat).
 */
export function weekAhead(
  orders: DashOrder[],
  rfqs: DashRfq[],
  requisitions: RequisitionCard[],
  today: string,
  n = 7,
): WeekAhead {
  const last = addDays(today, n - 1);
  const items: WeekItem[] = [];
  for (const o of orders) {
    if (o.completedAt || o.delivered || !o.deliveryDate || o.deliveryDate > last) continue;
    items.push({
      key: `o-${o.id}`,
      kind: 'delivery',
      day: o.deliveryDate,
      title: o.supplier.name,
      detail: `${o.number} · ${o.site.name}`,
      lei: o.valueLei,
      rfqId: o.rfqId,
      orderId: o.id,
    });
  }
  for (const r of rfqs) {
    if (!r.closed && r.deadline >= today && r.deadline <= last) {
      items.push({
        key: `d-${r.id}`,
        kind: 'deadline',
        day: r.deadline,
        title: r.title,
        detail: tr('{0} din {1} oferte', r.offers, r.sends),
        lei: null,
        rfqId: r.id,
      });
    }
    if (!r.ordered && r.neededBy && r.neededBy <= last) {
      items.push({
        key: `n-${r.id}`,
        kind: 'needed',
        day: r.neededBy,
        title: r.title,
        detail: `${r.site.name} · ${r.orderPending ? tr('comanda așteaptă aprobare') : tr('fără comandă')}`,
        lei: null,
        rfqId: r.id,
      });
    }
  }
  for (const q of requisitions) {
    if (!q.neededBy || q.neededBy > last) continue;
    items.push({
      key: `q-${q.id}`,
      kind: 'needed',
      day: q.neededBy,
      title: q.title,
      detail: tr('{0} · {1} · nepreluat', q.number, q.siteName),
      lei: null,
      requisitionId: q.id,
    });
  }

  const days = Array.from({ length: n }, (_, i) => ({ day: addDays(today, i), items: [] as WeekItem[] }));
  const byDay = new Map(days.map((d) => [d.day, d]));
  const overdue: WeekItem[] = [];
  const order: Record<WeekKind, number> = { delivery: 0, needed: 1, deadline: 2 };
  for (const it of items.sort((a, b) => order[a.kind] - order[b.kind])) {
    if (it.day < today) overdue.push(it);
    else byDay.get(it.day)?.items.push(it);
  }
  const ahead = items.filter((i) => i.day >= today);
  const deliveries = ahead.filter((i) => i.kind === 'delivery');
  return {
    days,
    overdue: overdue.sort((a, b) => a.day.localeCompare(b.day)),
    deliveryLei: deliveries.reduce((s, i) => s + (i.lei ?? 0), 0),
    deliveries: deliveries.length,
    deadlines: ahead.filter((i) => i.kind === 'deadline').length,
    needed: ahead.filter((i) => i.kind === 'needed').length,
  };
}

/**
 * Câte zile trec, la mijloc (mediana), de la cerere la comanda emisă, pe
 * comenzile din ultimele `sinceDays` zile. null fără nicio comandă cu dată.
 */
export function cycleDays(orders: DashOrder[], now: number, sinceDays = 90): number | null {
  const from = now - sinceDays * DAY;
  const d = orders
    .filter((o) => o.kind === 'order' && o.rfqCreatedAt && Date.parse(o.issuedAt) >= from)
    .map((o) => Math.max(0, (Date.parse(o.issuedAt) - Date.parse(o.rfqCreatedAt!)) / DAY))
    .sort((a, b) => a - b);
  if (!d.length) return null;
  const mid = Math.floor(d.length / 2);
  const m = d.length % 2 ? d[mid]! : (d[mid - 1]! + d[mid]!) / 2;
  return Math.round(m * 10) / 10;
}

/** Cererile deschise: câți furnizori au răspuns din câți au primit cererea. */
export function responseRate(rfqs: DashRfq[]): { answered: number; asked: number } {
  let answered = 0;
  let asked = 0;
  for (const r of rfqs) {
    if (r.closed) continue;
    asked += r.sent;
    answered += r.offers;
  }
  return { answered, asked };
}

export interface SupplierMoney {
  supplier: SupplierRef;
  total: number;
  orders: number;
}

/** Furnizorii după cât li s-a comandat (achiziționat și angajat), cel mai mare primul. */
export function moneyBySupplier(orders: DashOrder[]): SupplierMoney[] {
  const map = new Map<string, SupplierMoney>();
  for (const o of orders) {
    const row = map.get(o.supplier.id) ?? { supplier: o.supplier, total: 0, orders: 0 };
    row.total += o.valueLei;
    row.orders += 1;
    map.set(o.supplier.id, row);
  }
  return [...map.values()].sort(
    (a, b) => b.total - a.total || a.supplier.name.localeCompare(b.supplier.name, 'ro'),
  );
}

/* ======================== perioada, economiile, furnizorii ======================== */

/** O cerere, cât îi trebuie panoului ca să socotească orice perioadă și orice șantier. */
export interface RfqStat {
  id: string;
  title: string;
  category: string;
  site: { id: string; name: string };
  /** ISO 8601 */
  createdAt: string;
  closedAt: string | null;
  /** ISO 8601, când s-a ales furnizorul */
  chosenAt: string | null;
  chosenSupplierId: string | null;
  /** valoarea trecută la alegere; null = nu s-a scris */
  chosenTotal: number | null;
  /** ISO 8601, prima comandă emisă din cerere; null = necomandată */
  orderedAt: string | null;
  sends: {
    supplierId: string;
    supplier: string;
    sentAt: string | null;
    receivedAt: string | null;
    /** totalul ofertei complete, pe toată cantitatea; null = fără ofertă sau incompletă */
    total: number | null;
  }[];
  /** cea mai mică ofertă completă; null fără nicio ofertă completă */
  best: number | null;
  /** a doua ofertă completă; null cu mai puțin de două */
  next: number | null;
  /** totalul pe cel mai mic preț la fiecare poziție (combinând furnizorii); null dacă nu se poate */
  mix: number | null;
}

export type RangeKind = 'month' | '30d' | '90d' | 'year' | 'custom';

export interface Range {
  /** 'AAAA-LL-ZZ', inclusiv */
  from: string;
  to: string;
  /** perioada de dinainte, la fel de lungă: pentru tendințe */
  prevFrom: string;
  prevTo: string;
}

const daysBetween = (a: string, b: string) => Math.round((at(b) - at(a)) / DAY);

/** Perioada aleasă, plus cea de dinainte cu aceeași lungime. */
export function rangeOf(kind: RangeKind, today: string, custom?: { from: string; to: string }): Range {
  let from = today;
  let to = today;
  if (kind === 'month') from = `${today.slice(0, 8)}01`;
  else if (kind === '30d') from = addDays(today, -29);
  else if (kind === '90d') from = addDays(today, -89);
  else if (kind === 'year') from = `${today.slice(0, 4)}-01-01`;
  else if (custom && custom.from && custom.to) {
    from = custom.from <= custom.to ? custom.from : custom.to;
    to = custom.from <= custom.to ? custom.to : custom.from;
  }
  const len = daysBetween(from, to) + 1;
  return { from, to, prevFrom: addDays(from, -len), prevTo: addDays(from, -1) };
}

const localIso = (t: string) => {
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Momentul (ISO, sau o zi 'AAAA-LL-ZZ') cade în perioadă? Ziua e cea locală a browserului. */
export const inRange = (t: string | null | undefined, from: string, to: string): boolean => {
  if (!t) return false;
  const d = t.length > 10 ? localIso(t) : t;
  return d >= from && d <= to;
};

/** Cel mai bun preț posibil pe cerere: combinația, dacă iese sub cea mai bună ofertă întreagă. */
export const bestAchievable = (r: RfqStat): number | null =>
  r.best == null ? null : r.mix != null && r.mix < r.best ? r.mix : r.best;

/**
 * Economia posibilă la o cerere: cât e cel mai bun preț posibil sub a doua ofertă
 * completă (regula proprietarului: nu față de cea mai scumpă, un preț umflat
 * n-ar fi o economie). Fără două oferte complete nu există comparație: 0.
 */
export function potentialOf(r: RfqStat): number {
  const b = bestAchievable(r);
  if (r.next == null || b == null) return 0;
  return Math.max(0, r.next - b);
}

/**
 * Economia realizată: cererea are furnizor ales, iar prețul lui (oferta completă
 * a furnizorului ales sau valoarea scrisă la alegere) e sub cea mai bună dintre
 * celelalte oferte complete. Peste ea, 0 (nu e economie).
 */
export function realizedOf(r: RfqStat): number {
  if (!r.chosenSupplierId) return 0;
  const own = r.sends.find((s) => s.supplierId === r.chosenSupplierId)?.total ?? r.chosenTotal;
  const others = r.sends
    .filter((s) => s.supplierId !== r.chosenSupplierId && s.total != null)
    .map((s) => s.total!);
  if (own == null || !others.length) return 0;
  return Math.max(0, Math.min(...others) - own);
}

export interface SavingsSummary {
  /** economia posibilă pe toate cererile cu comparație din perioadă */
  potential: number;
  /** la câte cereri */
  potentialCount: number;
  /** economia obținută pe cererile hotărâte */
  realized: number;
  realizedCount: number;
  /** cât din economia posibilă a cererilor hotărâte s-a și obținut; null fără cereri hotărâte */
  captured: number | null;
  /** cele mai mari economii posibile, pentru „oportunități” */
  top: { rfq: RfqStat; potential: number; decided: boolean }[];
  /** suma celor de-a doua oferte: prețul de referință */
  quoted: number;
}

export function savingsOf(stats: RfqStat[]): SavingsSummary {
  let potential = 0;
  let potentialCount = 0;
  let realized = 0;
  let realizedCount = 0;
  let decidedPotential = 0;
  let quoted = 0;
  const top: SavingsSummary['top'] = [];
  for (const r of stats) {
    if (r.next == null) continue;
    const p = potentialOf(r);
    potential += p;
    potentialCount += 1;
    quoted += r.next;
    if (p > 0) top.push({ rfq: r, potential: p, decided: !!r.chosenSupplierId });
    if (r.chosenSupplierId) {
      decidedPotential += p;
      const g = realizedOf(r);
      realized += g;
      if (g > 0) realizedCount += 1;
    }
  }
  top.sort((a, b) => b.potential - a.potential);
  return {
    potential,
    potentialCount,
    realized,
    realizedCount,
    captured: decidedPotential > 0 ? Math.min(1, realized / decidedPotential) : null,
    top: top.slice(0, 3),
    quoted,
  };
}

export interface PerfMonth {
  /** 'AAAA-LL' */
  key: string;
  label: string;
  name: string;
  /** a doua ofertă completă, adunată: prețul la care ai fi cumpărat fără comparație */
  reference: number;
  /** cel mai bun preț posibil, adunat */
  best: number;
  rfqs: number;
}

/** Pe luni (luna cererii): prețul de referință față de cel mai bun preț posibil. */
export function performance(stats: RfqStat[], to: string, months = 7): PerfMonth[] {
  const end = new Date(`${to}T12:00:00`);
  const out: PerfMonth[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(end.getFullYear(), end.getMonth() - i, 1);
    out.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString(intlLocale(), { month: 'short' }),
      name: d.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' }),
      reference: 0,
      best: 0,
      rfqs: 0,
    });
  }
  const byKey = new Map(out.map((m) => [m.key, m]));
  for (const r of stats) {
    const b = bestAchievable(r);
    if (r.next == null || b == null) continue;
    const m = byKey.get(localIso(r.createdAt).slice(0, 7));
    if (!m) continue;
    m.reference += r.next;
    m.best += b;
    m.rfqs += 1;
  }
  return out;
}

export interface OrderedMonth {
  /** 'AAAA-LL' */
  key: string;
  label: string;
  name: string;
  /** valoarea comenzilor emise în lună, lei */
  lei: number;
  orders: number;
}

/** Valoarea comandată pe luni (luna emiterii), ultimele `months` luni până la `to`. */
export function orderedByMonth(orders: DashOrder[], to: string, months = 12): OrderedMonth[] {
  const end = new Date(`${to}T12:00:00`);
  const out: OrderedMonth[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(end.getFullYear(), end.getMonth() - i, 1);
    out.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString(intlLocale(), { month: 'short' }),
      name: d.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' }),
      lei: 0,
      orders: 0,
    });
  }
  const byKey = new Map(out.map((m) => [m.key, m]));
  for (const o of orders) {
    const m = byKey.get(localIso(o.issuedAt).slice(0, 7));
    if (!m) continue;
    m.lei += o.valueLei;
    m.orders += 1;
  }
  return out;
}

export interface CategorySpend {
  /** null = „Altele”, categoriile mici adunate */
  category: string | null;
  lei: number;
  share: number;
  orders: number;
}

/** Valoarea comandată în perioadă pe categorii: primele `top`, restul adunate în „Altele”. */
export function orderedByCategory(orders: DashOrder[], from: string, to: string, top = 5): CategorySpend[] {
  const map = new Map<string, { lei: number; orders: number }>();
  for (const o of orders) {
    if (!inRange(o.issuedAt, from, to) || o.valueLei <= 0) continue;
    const k = o.category.trim() || tr('Fără categorie');
    const x = map.get(k) ?? { lei: 0, orders: 0 };
    x.lei += o.valueLei;
    x.orders += 1;
    map.set(k, x);
  }
  const rows = [...map].map(([category, x]) => ({ category, ...x })).sort((a, b) => b.lei - a.lei);
  const total = rows.reduce((n, x) => n + x.lei, 0);
  const keep: { category: string | null; lei: number; orders: number }[] =
    rows.length > top ? rows.slice(0, top - 1) : rows;
  if (rows.length > top) {
    const rest = rows.slice(top - 1);
    keep.push({
      category: null,
      lei: rest.reduce((n, x) => n + x.lei, 0),
      orders: rest.reduce((n, x) => n + x.orders, 0),
    });
  }
  return keep.map((x) => ({ ...x, share: total ? x.lei / total : 0 }));
}

export interface RecurringNeed {
  category: string;
  /** de câte ori s-a comandat (zile distincte) */
  times: number;
  /** la câte zile, la mijloc (mediana), între comenzi */
  everyDays: number;
  /** 'AAAA-LL-ZZ': ultima comandă */
  lastDay: string;
  /** 'AAAA-LL-ZZ': când ar urma, după ritmul de până acum */
  nextDay: string;
  /** valoarea obișnuită a unei comenzi (mediana), lei */
  typicalLei: number;
  /** cererea ultimei comenzi, ca s-o poți repeta */
  lastRfqId: string;
  lastSupplier: string;
}

const median = (xs: number[]): number => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

/**
 * Ce se cumpără des și probabil urmează: pe categorie, din comenzile emise.
 * Doar categoriile comandate în cel puțin 3 zile diferite, cu un ritm de
 * peste 5 zile; nimic dacă există deja o cerere deschisă pe categorie, făcută
 * după ultima comandă. Arată ce vine în `horizon` zile și ce a trecut de
 * curând (cel mult un ritm sau 30 de zile în urmă), cel mai apropiat primul.
 */
export function recurringNeeds(
  orders: DashOrder[],
  rfqs: DashRfq[],
  today: string,
  horizon = 30,
): RecurringNeed[] {
  const byCat = new Map<string, DashOrder[]>();
  for (const o of orders) {
    if (o.kind !== 'order') continue;
    const k = o.category.trim();
    if (!k) continue;
    byCat.set(k, [...(byCat.get(k) ?? []), o]);
  }
  const out: RecurringNeed[] = [];
  const last = addDays(today, horizon);
  for (const [category, list] of byCat) {
    const sorted = [...list].sort((a, b) => a.issuedAt.localeCompare(b.issuedAt));
    const days = [...new Set(sorted.map((o) => localIso(o.issuedAt)))];
    if (days.length < 3) continue;
    const gaps = days.slice(1).map((d, i) => Math.round((at(d) - at(days[i]!)) / DAY));
    const every = Math.round(median(gaps));
    if (every < 5) continue;
    const lastDay = days[days.length - 1]!;
    const nextDay = addDays(lastDay, every);
    if (nextDay > last || nextDay < addDays(today, -Math.min(every, 30))) continue;
    const open = rfqs.some(
      (r) => r.category.trim() === category && !r.ordered && localIso(r.createdAt) >= lastDay,
    );
    if (open) continue;
    const lastOrder = sorted[sorted.length - 1]!;
    out.push({
      category,
      times: days.length,
      everyDays: every,
      lastDay,
      nextDay,
      typicalLei: median(list.map((o) => o.valueLei)),
      lastRfqId: lastOrder.rfqId,
      lastSupplier: lastOrder.supplier.name,
    });
  }
  return out.sort((a, b) => a.nextDay.localeCompare(b.nextDay));
}

export interface SupplierInsight {
  supplierId: string;
  supplier: string;
  /** cereri primite (trimise în perioadă) */
  asked: number;
  offers: number;
  /** oferte / cereri primite */
  rate: number;
  /** zile, la mijloc, de la trimitere la ofertă; null fără date */
  responseDays: number | null;
  /** suma ofertelor complete, lei */
  quoted: number;
  /** de câte ori a avut cea mai mică ofertă completă, din câte comparații */
  cheapest: number;
  compared: number;
  position: 'strong' | 'average' | 'weak' | 'new';
}

/** Furnizorii: cât răspund, cât de repede și cât de des au cel mai bun preț. */
export function supplierInsights(stats: RfqStat[], from: string, to: string): SupplierInsight[] {
  const map = new Map<string, SupplierInsight & { times: number[] }>();
  for (const r of stats) {
    const complete = r.sends.filter((s) => s.total != null);
    const low = complete.length >= 2 ? Math.min(...complete.map((s) => s.total!)) : null;
    for (const s of r.sends) {
      if (!inRange(s.sentAt ?? s.receivedAt, from, to)) continue;
      const row = map.get(s.supplierId) ?? {
        supplierId: s.supplierId,
        supplier: s.supplier,
        asked: 0,
        offers: 0,
        rate: 0,
        responseDays: null,
        quoted: 0,
        cheapest: 0,
        compared: 0,
        position: 'new' as const,
        times: [] as number[],
      };
      row.asked += 1;
      if (s.receivedAt) {
        row.offers += 1;
        if (s.sentAt) row.times.push(Math.max(0, (Date.parse(s.receivedAt) - Date.parse(s.sentAt)) / DAY));
      }
      if (s.total != null) row.quoted += s.total;
      if (low != null && s.total != null) {
        row.compared += 1;
        if (Math.abs(s.total - low) < 0.5) row.cheapest += 1;
      }
      map.set(s.supplierId, row);
    }
  }
  return [...map.values()]
    .map(({ times, ...r }): SupplierInsight => {
      const t = [...times].sort((a, b) => a - b);
      const mid = Math.floor(t.length / 2);
      const share = r.compared ? r.cheapest / r.compared : null;
      return {
        ...r,
        rate: r.asked ? r.offers / r.asked : 0,
        responseDays: t.length
          ? Math.round((t.length % 2 ? t[mid]! : (t[mid - 1]! + t[mid]!) / 2) * 10) / 10
          : null,
        position: share == null ? 'new' : share >= 0.5 ? 'strong' : share >= 0.2 ? 'average' : 'weak',
      };
    })
    .sort((a, b) => b.offers - a.offers || b.quoted - a.quoted);
}

export interface FlowCounts {
  rfqs: number;
  contacted: number;
  offers: number;
  compared: number;
  decided: number;
  ordered: number;
}

/** Fluxul perioadei: cererile făcute și, din ele, cât au ajuns la fiecare pas. */
export function flowOf(stats: RfqStat[]): FlowCounts {
  const f: FlowCounts = { rfqs: 0, contacted: 0, offers: 0, compared: 0, decided: 0, ordered: 0 };
  for (const r of stats) {
    f.rfqs += 1;
    f.contacted += r.sends.filter((s) => s.sentAt || s.receivedAt).length;
    f.offers += r.sends.filter((s) => s.receivedAt).length;
    if (r.sends.filter((s) => s.total != null).length >= 2) f.compared += 1;
    if (r.chosenSupplierId) f.decided += 1;
    if (r.orderedAt) f.ordered += 1;
  }
  return f;
}

/** Schimbarea în procente față de perioada de dinainte; null când atunci a fost zero. */
export const trendPct = (now: number, prev: number): number | null =>
  prev > 0 ? Math.round(((now - prev) / prev) * 100) : null;
